import { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  buildInvitationEmailHtml,
  buildInvitationEmailSubject,
  buildEventAddedEmailHtml,
  buildEventAddedEmailSubject,
  buildEventDownloadLink,
  buildEventOpenLink,
  EMAIL_ASSET_BASE_URL,
} from "@event-planner/shared";
import { ScaledEmailFrame } from "./phone/ScaledEmailFrame";

/**
 * The templates reference brand assets on the production admin origin. The
 * admin serves the same files from its public folder, so pointing them at
 * the current origin makes the preview work in dev (Vite) and prod alike.
 */
function localizeAssets(html: string): string {
  if (typeof window === "undefined") return html;
  return html.split(EMAIL_ASSET_BASE_URL).join(window.location.origin);
}

export type InvitationEmailVariant = "invitation" | "added";

/** Shown when previewing the code variant for someone who has no code yet. */
export const SAMPLE_INVITATION_CODE = "ABCD2345";

export interface InvitationEmailPreviewEvent {
  eventId: string;
  eventName: string;
  eventStartDate: string;
  eventEndDate: string;
  eventLocation: string | null;
}

interface InvitationEmailPreviewProps {
  event: InvitationEmailPreviewEvent;
  recipientName: string | null;
  /** Address the invitation is registered under; printed in the sign-in tips. */
  recipientEmail: string;
  /** Per-event code for pending people; null for active attendees. */
  invitationCode: string | null;
  variant: InvitationEmailVariant;
  height?: number | string;
}

/**
 * Renders the real email HTML (the same shared builders the Cloud Function
 * uses) inside a sandboxed iframe. What the organizer sees here is what the
 * recipient gets, minus the inline-attached hero (the preview loads the hero
 * from its public URL instead).
 */
export function InvitationEmailPreview({
  event,
  recipientName,
  recipientEmail,
  invitationCode,
  variant,
  height = 420,
}: InvitationEmailPreviewProps) {
  const usingSampleCode = variant === "invitation" && !invitationCode;

  const { subject, html } = useMemo(() => {
    if (variant === "invitation") {
      return {
        subject: buildInvitationEmailSubject(event.eventName),
        html: localizeAssets(buildInvitationEmailHtml({
          recipientName,
          recipientEmail,
          eventName: event.eventName,
          eventStartDate: event.eventStartDate,
          eventEndDate: event.eventEndDate,
          eventLocation: event.eventLocation,
          invitationCode: invitationCode ?? SAMPLE_INVITATION_CODE,
          eventDownloadLink: buildEventDownloadLink(event.eventId),
        })),
      };
    }
    return {
      subject: buildEventAddedEmailSubject(event.eventName),
      html: localizeAssets(buildEventAddedEmailHtml({
        recipientName,
        eventName: event.eventName,
        eventStartDate: event.eventStartDate,
        eventEndDate: event.eventEndDate,
        eventLocation: event.eventLocation,
        appDeepLink: buildEventOpenLink(event.eventId),
      })),
    };
  }, [event, recipientName, recipientEmail, invitationCode, variant]);

  return (
    <Box data-testid="invitation-email-preview">
      <Typography variant="body2" sx={{ mb: 1 }}>
        <Box component="span" sx={{ color: "text.secondary" }}>
          Subject:{" "}
        </Box>
        <Box component="span" sx={{ fontWeight: 500 }} data-testid="invitation-email-preview-subject">
          {subject}
        </Box>
      </Typography>
      <Box
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden",
          height,
          bgcolor: "#f5f5f5",
        }}
      >
        <ScaledEmailFrame srcDoc={html} title="Invitation email preview" sandbox="" />
      </Box>
      {usingSampleCode && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          Sample code shown. Each recipient receives their own code.
        </Typography>
      )}
    </Box>
  );
}
